import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  BarChart3,
  ChevronRight,
  Trophy,
  IndianRupee,
  ShoppingCart,
} from 'lucide-react';

interface NavItem {
  name: string;
  path: string;
  icon: React.ReactNode;
  description?: string;
}

const ReportsSidebar = () => {
  const location = useLocation();
  const [isCollapsed, setIsCollapsed] = useState(false);

  const navItems: NavItem[] = [
    {
      name: 'ABC Report',
      path: 'abc',
      icon: <ShoppingCart className="h-4 w-4" />,
      description: 'GoKwik & Shopify funnel from logs'
    },
    // {
    //   name: 'Orders Summary',
    //   path: 'summary',
    //   icon: <BarChart3 className="h-4 w-4" />,
    //   description: 'KPI overview & top performers'
    // },
    // {
    //   name: 'Date-wise Trend',
    //   path: 'date-wise',
    //   icon: <TrendingUp className="h-4 w-4" />,
    //   description: 'Orders trend over time'
    // },
    // {
    //   name: 'By Tag',
    //   path: 'tag-wise',
    //   icon: <Tag className="h-4 w-4" />,
    //   description: 'Orders by managed tags'
    // },
    // {
    //   name: 'By Agent',
    //   path: 'agent-wise',
    //   icon: <Users className="h-4 w-4" />,
    //   description: 'Orders by agent name'
    // },
    // {
    //   name: 'Status Matrix',
    //   path: 'status-matrix',
    //   icon: <Grid3x3 className="h-4 w-4" />,
    //   description: 'Financial × Fulfillment'
    // },
    {
      name: 'Incentive Leaderboard',
      path: 'incentive-leaderboard',
      icon: <IndianRupee className="h-4 w-4" />,
      description: 'Agent incentive & delivery stats'
    },
    {
      name: 'Agent Performance',
      path: 'agent-performance',
      icon: <Trophy className="h-4 w-4" />,
      description: 'Leads, conversions & response time'
    }
  ];

  const isPathActive = (itemPath: string) => {
    if (location.pathname.endsWith('/reports')) {
      return itemPath === 'abc';
    }

    const currentPath = location.pathname.replace('/reports/', '');
    return currentPath === itemPath;
  };

  const getFullPath = (path: string) => {
    return `/reports/${path}`;
  };

  return (
    <div className={`${isCollapsed ? 'w-16' : 'w-72'} h-full bg-white dark:bg-gray-900 border-r border-gray-200 dark:border-gray-700 transition-all duration-300 flex flex-col`}>
      {/* Header */}
      <div className="p-4 border-b border-gray-200 dark:border-gray-700">
        <div className="flex items-center justify-between">
          <div className={`flex items-center space-x-3 ${isCollapsed ? 'hidden' : ''}`}>
            <div className="bg-brandColor p-2 rounded-lg">
              <BarChart3 className="h-5 w-5 text-white" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Reports</h2>
              <p className="text-xs text-gray-500 dark:text-gray-400">Orders analytics</p>
            </div>
          </div>
          <button
            onClick={() => setIsCollapsed(!isCollapsed)}
            className="p-1.5 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
            title={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            <ChevronRight className={`h-4 w-4 text-gray-500 transition-transform ${!isCollapsed ? 'rotate-180' : ''}`} />
          </button>
        </div>
      </div>

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto p-3 space-y-1">
        {navItems.map((item) => {
          const isActive = isPathActive(item.path);

          return (
            <Link
              key={item.path}
              to={getFullPath(item.path)}
              className={`
                relative group flex items-center px-3 py-2 rounded-lg transition-all duration-200
                ${isActive
                  ? 'bg-blue-50 dark:bg-blue-900/20 text-brandColor dark:text-blue-400 border-l-4 border-brandColor'
                  : 'hover:bg-gray-50 dark:hover:bg-gray-800 text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white'
                }
                ${!isCollapsed ? 'space-x-3' : 'justify-center'}
              `}
            >
              {/* Icon */}
              <div className={`flex-shrink-0 ${isActive ? 'text-brandColor dark:text-blue-400' : 'text-gray-400 group-hover:text-gray-600 dark:group-hover:text-gray-300'}`}>
                {item.icon}
              </div>

              {/* Content */}
              {!isCollapsed && (
                <div className="flex-1">
                  <span className="text-sm font-medium">{item.name}</span>
                  {item.description && (
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{item.description}</p>
                  )}
                </div>
              )}

              {/* Tooltip for collapsed state */}
              {isCollapsed && (
                <div className="absolute left-full ml-2 px-2 py-1 bg-gray-900 text-white text-xs rounded opacity-0 group-hover:opacity-100 pointer-events-none whitespace-nowrap z-10">
                  {item.name}
                </div>
              )}
            </Link>
          );
        })}
      </nav>
    </div>
  );
};

export default ReportsSidebar;
